import { InfoField } from "$components/info-field";
import { Page } from "$components/page";
import { formatDate, formatUserName } from "$routes/inventory/_lib/formatting";
import InventoryTitleRow from "$routes/inventory/_lib/inventory-title-row";
import StatusChip from "$routes/inventory/_lib/status-chip";
import { linkToInventoryMoveRequests } from "$routes/inventory/links";
import { gql } from "@apollo/client";
import { Box } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useActionedInventoryMoveRequestOverviewQuery } from "./__generated__/page.gql";
import ActionedMoveRequestOverviewTable from "./_lib/actioned-move-request-overview-table";

gql`
  query actionedInventoryMoveRequestOverview($input: InventoryMoveRequestInput!) {
    inventoryMoveRequest(input: $input) {
      id
      status
      createdAt
      requestor {
        person {
          firstname
          lastname
        }
      }
      actionedAt
      actionerNotes
      actioner {
        person {
          firstname
          lastname
        }
      }
      receivingLocation {
        name
      }

      skus {
        items {
          id
          quantityRequested
          sku {
            id
            name
            externalReferences {
              origin
            }
          }
          fulfillments {
            id
            quantityFulfilled
            fulfillingLot {
              expiryDate
            }
          }
        }
      }
    }
  }
`;

const ActionedMoveRequestOverview: FC = () => {
  const { moveRequestId } = useParams<{ moveRequestId: string }>();
  const { formatMessage } = useIntl();

  const { data, loading } = useActionedInventoryMoveRequestOverviewQuery({
    variables: {
      input: {
        id: moveRequestId,
      },
    },
  });

  const request = data?.inventoryMoveRequest;

  return (
    <Page data-dense>
      <InventoryTitleRow
        backToLink={linkToInventoryMoveRequests()}
        title={
          <Box display="flex" alignItems="center">
            <FormattedMessage
              id="inventory.requests.overview.title"
              defaultMessage="Request"
            />
            <Box marginLeft={2}>
              <StatusChip status={request?.status} />
            </Box>
          </Box>
        }
      >
        <Box display="flex" flexWrap="wrap" alignItems="center">
          <InfoField
            id="requestedAt"
            label={formatMessage({
              id: "inventory.requests.overview.requestedDate",
              defaultMessage: "Requested",
            })}
            value={formatDate(request?.createdAt)}
            width="120px"
          />
          <InfoField
            id="requestedBy"
            label={formatMessage({
              id: "inventory.requests.overview.requestedBy",
              defaultMessage: "Requested By",
            })}
            value={formatUserName(request?.requestor)}
            minWidth="200px"
          />
          <InfoField
            id="actionedAt"
            label={formatMessage({
              id: "inventory.requests.overview.actinoedAt",
              defaultMessage: "Processed",
            })}
            value={formatDate(request?.actionedAt)}
            width="120px"
          />
          <InfoField
            id="actionedBy"
            label={formatMessage({
              id: "inventory.requests.overview.actionedBy",
              defaultMessage: "Processed By",
            })}
            value={formatUserName(request?.actioner)}
            minWidth="200px"
          />
          <InfoField
            id="requestedLocation"
            label={formatMessage({
              id: "inventory.requests.overview.requestedLocation",
              defaultMessage: "Location of Request",
            })}
            value={request?.receivingLocation?.name ?? ""}
            minWidth="300px"
          />
        </Box>
      </InventoryTitleRow>
      {request?.actionerNotes && (
        <Box marginTop={3}>
          <InfoField
            id="notes"
            label={formatMessage({
              id: "inventory.requests.overview.notes",
              defaultMessage: "Notes",
            })}
            value={request?.actionerNotes}
            multiline
            fullWidth
            rows={3}
          />
        </Box>
      )}
      <Box marginTop={3}>
        <ActionedMoveRequestOverviewTable
          loading={loading}
          data={request?.skus.items}
        />
      </Box>
    </Page>
  );
};

export default ActionedMoveRequestOverview;
